import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Bell,
  Send,
  Users,
  RefreshCw,
  Search,
  Check,
  X,
  Mail,
  MessageSquare,
  AlertTriangle,
  Info,
  Clock
} from "lucide-react";

const AdminNotifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Broadcast form state
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastTarget, setBroadcastTarget] = useState("all");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch notification stats
      const statsRes = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Fetch recent notifications/logs
      const logsRes = await fetch(`${API}/admin/logs?action=notification&limit=50`, { credentials: "include" });
      if (logsRes.ok) {
        const data = await logsRes.json();
        setNotifications(data);
      }

      // Fetch broadcasts history
      const broadcastsRes = await fetch(`${API}/admin/broadcasts`, { credentials: "include" });
      if (broadcastsRes.ok) {
        const data = await broadcastsRes.json();
        setBroadcasts(data.broadcasts || []);
      }
    } catch (error) {
      console.error("Failed to fetch notification data:", error);
      toast.error("Failed to load notification data");
    } finally {
      setLoading(false);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastMessage.trim()) {
      toast.error("Please fill in title and message");
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/admin/broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: broadcastTitle,
          message: broadcastMessage,
          target: broadcastTarget
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Broadcast sent to ${data.recipient_count} users`);
        setBroadcastTitle("");
        setBroadcastMessage("");
        setBroadcastTarget("all");
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to send broadcast");
      }
    } catch (error) {
      toast.error("Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-notifications-page">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                NOTIFICATION <span className="text-crimson">CENTER</span>
              </h1>
              <p className="text-gray-400">Manage platform notifications and broadcasts</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#333] text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {[
              { id: "overview", label: "Overview", icon: Bell },
              { id: "broadcast", label: "Send Broadcast", icon: Send },
              { id: "history", label: "History", icon: Clock },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`notification-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-crimson text-white"
                    : "bg-obsidian-light text-gray-400 hover:text-white"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="text-xs">Total Users</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{stats?.users?.total || 0}</div>
                </motion.div>
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Bell className="w-4 h-4" />
                    <span className="text-xs">Push Subscribers</span>
                  </div>
                  <div className="text-2xl font-bold text-green-400">--</div>
                </motion.div>
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Mail className="w-4 h-4" />
                    <span className="text-xs">Email Enabled</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-400">--</div>
                </motion.div>
                <motion.div
                  className="bg-obsidian-light border border-[#222] p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-xs">Messages Sent</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-400">{stats?.messages || 0}</div>
                </motion.div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-obsidian-light border border-[#222] p-6">
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    System Alerts
                  </h3>
                  <p className="text-gray-400 text-sm">Send urgent platform-wide notifications</p>
                  <Button
                    onClick={() => setActiveTab("broadcast")}
                    className="mt-4 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                  >
                    Create Alert
                  </Button>
                </div>
                <div className="bg-obsidian-light border border-[#222] p-6">
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-400" />
                    Announcement
                  </h3>
                  <p className="text-gray-400 text-sm">Send feature updates or platform news</p>
                  <Button
                    onClick={() => setActiveTab("broadcast")}
                    className="mt-4 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                  >
                    Create Announcement
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Broadcast Tab */}
          {activeTab === "broadcast" && (
            <motion.div
              className="max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="bg-obsidian-light border border-[#222] p-6">
                <h3 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                  <Send className="w-5 h-5 text-crimson" />
                  Send Broadcast Notification
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Target Audience</label>
                    <select
                      value={broadcastTarget}
                      onChange={(e) => setBroadcastTarget(e.target.value)}
                      className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="all">All Users</option>
                      <option value="jobseekers">Job Seekers Only</option>
                      <option value="recruiters">Recruiters Only</option>
                      <option value="paid">Paid Users Only</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Notification Title</label>
                    <Input
                      value={broadcastTitle}
                      onChange={(e) => setBroadcastTitle(e.target.value)}
                      placeholder="Enter notification title..."
                      className="bg-obsidian border-[#333] text-white"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Message</label>
                    <textarea
                      value={broadcastMessage}
                      onChange={(e) => setBroadcastMessage(e.target.value)}
                      placeholder="Enter your message..."
                      rows={4}
                      className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white resize-none"
                    />
                  </div>

                  <div className="pt-4 border-t border-[#333]">
                    <p className="text-sm text-gray-500 mb-4">
                      This will send a notification to {broadcastTarget === "all" ? "all" : broadcastTarget} users on the platform.
                    </p>
                    <Button
                      onClick={sendBroadcast}
                      disabled={sending || !broadcastTitle.trim() || !broadcastMessage.trim()}
                      className="bg-crimson hover:bg-crimson/90 disabled:opacity-50"
                    >
                      {sending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Broadcast
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="bg-obsidian-light border border-[#222]">
              <div className="p-4 border-b border-[#333]">
                <h3 className="font-heading text-lg font-bold">Broadcast History</h3>
              </div>
              
              {broadcasts.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No broadcasts sent yet</p>
                </div>
              ) : (
                <div className="divide-y divide-[#222]">
                  {broadcasts.map((broadcast, i) => (
                    <div key={broadcast.broadcast_id || i} className="p-4 hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                          <Send className="w-5 h-5 text-crimson" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-white">{broadcast.title}</div>
                          <div className="text-sm text-gray-400 mt-1">{broadcast.message}</div>
                          <div className="text-xs text-gray-500 mt-2 flex items-center gap-3">
                            <span className="bg-crimson/20 text-crimson px-2 py-0.5 rounded">
                              {broadcast.target === "all" ? "All Users" : broadcast.target === "jobseekers" ? "Job Seekers" : "Recruiters"}
                            </span>
                            <span>{broadcast.recipient_count} recipients</span>
                            <span>{new Date(broadcast.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminNotifications;
